import React, { useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  podcastEpisodesApi,
  type PodcastEpisode,
  type UpsertEpisodePayload,
} from "../api/podcastEpisodesApi";
import { SectionCard } from "./SectionCard";

const QUERY_KEY = ["admin-podcast-episodes"];

const emptyForm = (): UpsertEpisodePayload => ({
  title: "",
  description: "",
  guest_name: "",
  guest_title: "",
  episode_number: null,
  thumbnail_url: "",
  spotify_url: "",
  youtube_url: "",
  topics: [],
  is_active: true,
  display_order: 0,
});

const episodeToForm = (ep: PodcastEpisode): UpsertEpisodePayload => ({
  title: ep.title,
  description: ep.description ?? "",
  guest_name: ep.guest_name ?? "",
  guest_title: ep.guest_title ?? "",
  episode_number: ep.episode_number,
  thumbnail_url: ep.thumbnail_url ?? "",
  spotify_url: ep.spotify_url ?? "",
  youtube_url: ep.youtube_url ?? "",
  topics: ep.topics,
  is_active: ep.is_active,
  display_order: ep.display_order,
});

interface EpisodeDialogProps {
  open: boolean;
  episode: PodcastEpisode | null;
  onClose: () => void;
  onSave: (payload: UpsertEpisodePayload) => void;
  saving: boolean;
}

const EpisodeDialog: React.FC<EpisodeDialogProps> = ({
  open,
  episode,
  onClose,
  onSave,
  saving,
}) => {
  const [form, setForm] = useState<UpsertEpisodePayload>(
    episode ? episodeToForm(episode) : emptyForm()
  );
  const [topicsInput, setTopicsInput] = useState(
    (episode?.topics ?? []).join(", ")
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      const base = episode ? episodeToForm(episode) : emptyForm();
      setForm(base);
      setTopicsInput((episode?.topics ?? []).join(", "));
      setUploadError(null);
    }
  }, [open, episode]);

  const set = (field: keyof UpsertEpisodePayload, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { upload_url, file_url } = await podcastEpisodesApi.createUploadUrl({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      set("thumbnail_url", file_url);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    const topics = topicsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    onSave({ ...form, topics });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{episode ? "Edit Episode" : "New Episode"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title *"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Guest Name"
              value={form.guest_name ?? ""}
              onChange={(e) => set("guest_name", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Guest Title"
              value={form.guest_title ?? ""}
              onChange={(e) => set("guest_title", e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Episode Number"
              type="number"
              value={form.episode_number ?? ""}
              onChange={(e) =>
                set("episode_number", e.target.value === "" ? null : Number(e.target.value))
              }
              fullWidth
              size="small"
            />
            <TextField
              label="Display Order"
              type="number"
              value={form.display_order ?? 0}
              onChange={(e) => set("display_order", Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Stack>

          {/* Thumbnail upload */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
              Thumbnail Image
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {form.thumbnail_url ? (
                <Avatar
                  src={form.thumbnail_url}
                  variant="rounded"
                  sx={{ width: 72, height: 72 }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 72, height: 72, bgcolor: "action.hover" }}>
                  <UploadFileOutlinedIcon />
                </Avatar>
              )}
              <Stack spacing={1}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                >
                  {uploading ? "Uploading…" : "Upload image"}
                </Button>
                {form.thumbnail_url && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => set("thumbnail_url", null)}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            </Stack>
            {uploadError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {uploadError}
              </Alert>
            )}
          </Box>

          <TextField
            label="Spotify URL"
            value={form.spotify_url ?? ""}
            onChange={(e) => set("spotify_url", e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="YouTube URL"
            value={form.youtube_url ?? ""}
            onChange={(e) => set("youtube_url", e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Topics (comma-separated)"
            value={topicsInput}
            onChange={(e) => setTopicsInput(e.target.value)}
            fullWidth
            size="small"
            helperText="e.g. nutrition, recovery, sleep"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active ?? true}
                onChange={(e) => set("is_active", e.target.checked)}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading || !form.title.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PodcastEpisodesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PodcastEpisode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<PodcastEpisode | null>(null);

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => podcastEpisodesApi.list(),
    staleTime: 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: UpsertEpisodePayload) => podcastEpisodesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<UpsertEpisodePayload> }) =>
      podcastEpisodesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => podcastEpisodesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDeleteConfirm(null);
    },
  });

  const handleSave = (payload: UpsertEpisodePayload) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const episodes = listQuery.data?.data ?? [];

  return (
    <Box>
      <Stack spacing={3}>
        <SectionCard
          title="Podcast Episodes"
          description="Manage episodes shown in the mobile app. Episodes are displayed in display order ascending."
        >
          <Stack spacing={2}>
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                Add Episode
              </Button>
            </Box>

            {listQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading episodes…</Typography>
              </Stack>
            ) : listQuery.isError ? (
              <Alert severity="error">Failed to load episodes.</Alert>
            ) : episodes.length === 0 ? (
              <Alert severity="info">No episodes yet. Add one above.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>Episode</TableCell>
                    <TableCell>Guest</TableCell>
                    <TableCell align="center">Order</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {episodes.map((ep) => (
                    <TableRow key={ep.id} hover>
                      <TableCell>
                        <Avatar
                          src={ep.thumbnail_url ?? undefined}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        >
                          {ep.episode_number ?? "?"}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {ep.episode_number != null ? `Ep ${ep.episode_number} · ` : ""}
                          {ep.title}
                        </Typography>
                        {ep.topics.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                            {ep.topics.slice(0, 3).map((t) => (
                              <Chip key={t} label={t} size="small" variant="outlined" />
                            ))}
                            {ep.topics.length > 3 && (
                              <Chip
                                label={`+${ep.topics.length - 3}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ep.guest_name ?? "-"}</Typography>
                        {ep.guest_title && (
                          <Typography variant="caption" color="text.secondary">
                            {ep.guest_title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{ep.display_order}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={ep.is_active}
                          onChange={(e) =>
                            updateMutation.mutate({
                              id: ep.id,
                              payload: { is_active: e.target.checked },
                            })
                          }
                          disabled={updateMutation.isPending}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditing(ep);
                                setDialogOpen(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirm(ep)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </SectionCard>
      </Stack>

      <EpisodeDialog
        open={dialogOpen}
        episode={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        saving={isSaving}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Episode</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
